import { getJobRequirementTeamMembers } from "@endpoints/hiringCompany/requirements";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { HiringCompanyType } from "@utils/constants";
import { colors } from "@utils/theme";
// @ts-ignore
import ProfileImg from "@assets/images/profilepic.png";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

type RequirementTeamMember = {
  _id: string;
  userId: string;
  firstName: string;
  lastName: string;
  profilePic?: string;
  email?: string;
  refNo?: string;
  userName?: string;
  hiringCompanyType?: number;
  jobSharedOn?: string;
};

const roleLabelByType: Record<number, string> = {
  [HiringCompanyType.SUPER_ADMIN]: "Super admin",
  [HiringCompanyType.ADMIN]: "Admin",
  [HiringCompanyType.MANAGER]: "Hiring manager",
  [HiringCompanyType.TEAM_MEMBER]: "Hiring team member",
};

const HiringTeam: React.FC = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState<RequirementTeamMember[]>([]);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        const { success, data } = await getJobRequirementTeamMembers(id);
        if (success && Array.isArray(data)) {
          setTeamMembers(data);
        } else {
          setTeamMembers([]);
        }
      } catch (error) {
        setTeamMembers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamMembers();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
        <LoadingAnimation color={colors.PrimaryBlack} />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 p-4 md:p-8 max-w-6xl mx-auto w-full">
      <h3 className="text-sm font-semibold text-charcoal mb-4">Hiring team</h3>

      {teamMembers.length === 0 ? (
        <div className="text-xs text-gray-500 border border-dashed border-gray-300 rounded-md p-4">
          No hiring team members have been added for this job requirement.
        </div>
      ) : (
        <div className="overflow-x-auto w-full">
          <table className="w-full table-auto text-sm">
            <thead className="border-b border-[#E0E0E0]">
              <tr>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black w-[30%] whitespace-nowrap">
                  Member
                </th>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black whitespace-nowrap">
                  Reference
                </th>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black w-[30%] whitespace-nowrap">
                  Email
                </th>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black whitespace-nowrap">
                  Shared on
                </th>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black whitespace-nowrap">
                  Role
                </th>
              </tr>
            </thead>
            <tbody>
              {teamMembers.map((member) => (
                <tr key={member._id} className="border-b">
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-2">
                      <img
                        src={member.profilePic || ProfileImg}
                        alt={`${member.firstName || ""} ${member.lastName || ""}`.trim()}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <span className="font-medium text-xs text-[#6F6F6F] whitespace-nowrap">
                        {`${member.firstName || ""} ${member.lastName || ""}`.trim() ||
                          member.userName ||
                          "-"}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-3 font-medium text-xs text-[#6F6F6F] whitespace-nowrap">
                    {member.refNo || "-"}
                  </td>
                  <td className="px-2 py-3 font-medium text-xs text-[#6F6F6F] whitespace-nowrap">
                    {member.email || "-"}
                  </td>
                  <td className="px-2 py-3 font-medium text-xs text-[#6F6F6F] whitespace-nowrap">
                    {member.jobSharedOn
                      ? dayjs(member.jobSharedOn).format("DD MMM YYYY")
                      : "-"}
                  </td>
                  <td className="px-2 py-3 font-medium text-xs text-[#6F6F6F] whitespace-nowrap capitalize">
                    {roleLabelByType[Number(member.hiringCompanyType)] || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HiringTeam;
